import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Crown, Check, X, Sparkles, ArrowRight, Flame, PartyPopper } from "lucide-react";
import { Dialog, DialogContent } from "./ui/dialog";
import { Button } from "./ui/button";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { PRICING_FALLBACK, formatPrice, withPricing } from "../lib/pricing";

const BENEFITS = [
  "Unlimited report generation",
  "Save daily entries",
  "Personal daily history",
  "Weekly / Monthly / Annual reports",
  "Bulk PDF for the whole month",
  "School profile & UDISE management",
  "Save time — focus on teaching",
  "Stay calm and organized",
];

export default function PremiumModal({ open, onClose, used = 7, freeLimit = 7 }) {
  const nav = useNavigate();
  const { isAuthed } = useAuth();
  const [pricing, setPricing] = useState(PRICING_FALLBACK);

  useEffect(() => {
    if (!open) return;
    api.subConfig().then((c) => setPricing(withPricing(c))).catch(() => {});
  }, [open]);

  const activate = () => {
    onClose && onClose();
    nav(isAuthed ? "/premium" : "/register");
  };

  const price = pricing.price_inr;
  const original = pricing.original_price_inr;
  const savings = pricing.savings_inr;
  const offerLabel = pricing.offer_label;
  const limitReached = used >= freeLimit;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent className="max-w-md p-0 overflow-hidden gap-0 rounded-2xl" data-testid="premium-modal">
        {/* Header */}
        <div className="bg-gradient-to-br from-emerald-700 via-emerald-600 to-orange-500 text-white p-6 text-center relative">
          <button onClick={onClose} className="absolute right-3 top-3 h-8 w-8 rounded-full bg-white/20 hover:bg-white/30 grid place-items-center" data-testid="premium-modal-close" aria-label="Close">
            <X size={16}/>
          </button>
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-300/95 text-amber-900 px-3 py-1 text-[11px] font-extrabold uppercase tracking-widest shadow-md" data-testid="premium-modal-offer-badge">
            <Flame size={12}/> {offerLabel}
          </span>
          <div className="mx-auto mt-3 h-14 w-14 rounded-full bg-white/20 grid place-items-center backdrop-blur">
            <Crown size={28} className="text-yellow-200"/>
          </div>
          {limitReached ? (
            <>
              <h2 className="mt-3 font-display font-extrabold text-2xl flex items-center justify-center gap-2">
                <PartyPopper size={22} className="text-yellow-200"/> 7 free reports done!
              </h2>
              <p className="mt-1 text-white/90 text-sm">Unlock Premium and save valuable time on your daily school work.</p>
            </>
          ) : (
            <>
              <h2 className="mt-3 font-display font-extrabold text-2xl">Activate Premium</h2>
              <p className="mt-1 text-white/90 text-sm">Save time · focus on teaching · stay calm and organized</p>
            </>
          )}
          <div className="mt-3 inline-flex items-center gap-1 rounded-full bg-white/15 backdrop-blur px-3 py-1 text-xs font-semibold" data-testid="premium-modal-used">
            Free reports used: {used} / {freeLimit}
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {/* Price card */}
          <div className="relative rounded-2xl border-2 border-emerald-700 bg-gradient-to-br from-emerald-50 to-amber-50 p-5 text-center shadow-sm">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-orange-500 text-white text-[10px] uppercase tracking-widest font-extrabold px-3 py-1 shadow">
              <span className="inline-flex items-center gap-1"><Flame size={11}/> Launch Offer</span>
            </div>
            <div className="text-xs uppercase tracking-widest font-bold text-emerald-800">Premium Annual Plan</div>
            <div className="mt-2 flex items-end justify-center gap-2" data-testid="premium-modal-price">
              <span className="text-slate-400 text-xl font-semibold line-through decoration-2 decoration-red-400" data-testid="premium-modal-original">
                ₹{formatPrice(original)}
              </span>
              <span className="font-display font-extrabold text-5xl text-emerald-800 leading-none">₹{formatPrice(price)}</span>
            </div>
            <div className="mt-1 text-xs font-semibold text-slate-600">/ year · one-time payment</div>
            <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-amber-100 border border-amber-300 text-amber-900 px-3 py-1 text-xs font-bold" data-testid="premium-modal-savings">
              💰 Save ₹{formatPrice(savings)}
            </div>
          </div>

          {/* Benefits — compact 2-col on mobile */}
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Everything you get</div>
            <ul className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1.5">
              {BENEFITS.map((b) => (
                <li key={b} className="flex items-start gap-2 text-sm text-slate-800">
                  <Check size={14} className="text-emerald-600 mt-1 shrink-0"/> <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="grid gap-2">
            <Button onClick={activate} className="h-12 bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-base rounded-xl" data-testid="premium-modal-activate">
              <Sparkles size={16}/> {isAuthed ? `Activate Premium – ₹${formatPrice(price)}/year` : `Get Launch Offer – ₹${formatPrice(price)}/year`} <ArrowRight size={16}/>
            </Button>
            <Button variant="ghost" onClick={onClose} className="h-11 text-slate-600" data-testid="premium-modal-later">Maybe later</Button>
            <p className="text-center text-[11px] text-slate-500">Secure payment · 1 year Premium access · No auto-renewal</p>
            {!isAuthed && (
              <div className="text-center text-xs text-slate-500 pt-1">
                Already registered? <Link to="/login" onClick={onClose} className="text-emerald-700 font-semibold hover:underline">Sign in</Link>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
